import axios from 'axios';
import { getDistrictHeaders, addDistrictToUrl } from '../utils/districtConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000,
});

// Request interceptor to add district headers and auth token to every request
apiClient.interceptors.request.use(
  (config) => {
    // Add district code to headers
    const districtHeaders = getDistrictHeaders();
    config.headers = {
      ...config.headers,
      ...districtHeaders
    };

    // Add authentication token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // For development, also add district as query parameter
    // Only add if not already present in params
    if (process.env.REACT_APP_ENVIRONMENT === 'development' && config.url) {
      // Check if district is already in params (explicitly passed by API call)
      if (!config.params || !config.params.district) {
        config.url = addDistrictToUrl(config.url);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Retry logic for network errors (handles Render free-tier cold starts)
apiClient.interceptors.request.use((config) => {
  config._retryCount = config._retryCount || 0;
  return config;
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    // Retry on network errors or server-unavailable (Render cold start) up to 3 times
    const isNetworkError = !error.response;
    const isServerUnavailable = error.response?.status === 503 || error.response?.status === 504;
    if ((isNetworkError || isServerUnavailable) && config && config._retryCount < 3) {
      config._retryCount += 1;
      const delay = config._retryCount * 5000; // 5s, 10s, 15s
      await new Promise((resolve) => setTimeout(resolve, delay));
      return apiClient(config);
    }

    if (error.response) {
      // Handle 401 Unauthorized - token expired or invalid
      if (error.response.status === 401) {
        // Clear auth data
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');

        // Redirect to login if not already there
        if (window.location.pathname !== '/login' && window.location.pathname !== '/survey') {
          window.location.href = '/login';
        }
      }

      // Server responded with error
      console.error('API Error:', error.response.data);
      throw new Error(error.response.data.message || 'An error occurred');
    } else if (error.request) {
      // Request made but no response
      console.error('Network Error:', error.request);
      throw new Error('Network error. Please check your connection.');
    } else {
      // Something else happened
      console.error('Error:', error.message);
      throw new Error(error.message);
    }
  }
);

const studentAPI = {
  // Get all students with filters
  getAll: async (params = {}) => {
    const response = await apiClient.get('/students', { params });
    return response.data;
  },

  // Get all students from the roster (completed + not-completed), annotated
  // with surveyCompleted
  getAllWithStatus: async (params = {}) => {
    const response = await apiClient.get('/students/all', { params });
    return response.data;
  },

  // Get single student by ID
  getById: async (studentID) => {
    const response = await apiClient.get(`/students/${studentID}`);
    return response.data;
  },

  // Get dashboard statistics
  getStatistics: async (params = {}) => {
    const response = await apiClient.get('/students/statistics', { params });
    return response.data;
  },

  // Get unique career clusters
  getClusters: async () => {
    const response = await apiClient.get('/students/clusters');
    return response.data;
  },

  // Get unique grade levels
  getGradeLevels: async () => {
    const response = await apiClient.get('/students/grade-levels');
    return response.data;
  },

  // Get unique campuses
  getCampuses: async () => {
    const response = await apiClient.get('/students/campuses');
    return response.data;
  },

  // Export students as CSV
  exportCSV: async (params = {}) => {
    const response = await apiClient.get('/students/export/csv', {
      params,
      responseType: 'blob'
    });
    return response;
  },
};

const surveyAPI = {
  // Get all survey questions
  getQuestions: async (district = null) => {
    const params = district ? { district } : {};
    const response = await apiClient.get('/survey/questions', { params });
    return response.data;
  },

  // Validate student ID
  validateStudentID: async (studentID, district = null) => {
    const params = district ? { district } : {};
    const response = await apiClient.get(`/survey/validate/${studentID}`, { params });
    return response.data;
  },

  // Analyze survey responses with AI
  analyzeResponses: async (studentID, responses, district = null) => {
    const response = await apiClient.post('/survey/analyze', {
      studentID,
      responses,
      district
    }, {
      timeout: 60000 // 60 seconds for AI analysis
    });
    return response.data;
  },

  // Finalize survey with user-selected clusters
  finalizeSurvey: async (studentID, responses, selectedClusters, llmRecommendations, district = null, customCareerChoice = null) => {
    const response = await apiClient.post('/survey/finalize', {
      studentID,
      responses,
      selectedClusters,
      llmRecommendations,
      district,
      customCareerChoice
    }, {
      timeout: 30000 // 30 seconds for finalize
    });
    return response.data;
  },
};

const resultsAPI = {
  // Download PDF of survey results
  downloadPDF: (studentID) => {
    return apiClient.get(`/results/download/${studentID}`, {
      responseType: 'blob'
    });
  },

  // Email PDF of survey results
  emailPDF: async (studentID, recipientEmail, recipientName) => {
    const response = await apiClient.post('/results/email', {
      studentID,
      recipientEmail,
      recipientName
    });
    return response.data;
  },

  // Get presigned S3 URLs for all stored PDFs for a student
  getPDFLinks: async (studentID) => {
    const response = await apiClient.get(`/results/pdfs/${studentID}`);
    return response.data;
  }
};

// Build query params shared by all report endpoints, joining campus/gradeLevel
// arrays into the comma-separated format the backend expects.
const buildReportParams = ({ startDate, endDate, campus, gradeLevel } = {}) => {
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (campus && campus.length) {
    params.campus = Array.isArray(campus) ? campus.join(',') : campus;
  }
  if (gradeLevel && gradeLevel.length) {
    params.gradeLevel = Array.isArray(gradeLevel) ? gradeLevel.join(',') : gradeLevel;
  }
  return params;
};

const reportsAPI = {
  // Get overall statistics
  getStatistics: async (filters = {}) => {
    const response = await apiClient.get('/reports/statistics', { params: buildReportParams(filters) });
    return response.data;
  },

  // Get career cluster distribution
  getClusterDistribution: async (filters = {}) => {
    const response = await apiClient.get('/reports/cluster-distribution', { params: buildReportParams(filters) });
    return response.data;
  },

  // Get grade level distribution
  getGradeLevelDistribution: async (filters = {}) => {
    const response = await apiClient.get('/reports/grade-distribution', { params: buildReportParams(filters) });
    return response.data;
  },

  // Get campus distribution
  getCampusDistribution: async (filters = {}) => {
    const response = await apiClient.get('/reports/campus-distribution', { params: buildReportParams(filters) });
    return response.data;
  },

  // Get popular activities
  getPopularActivities: async (filters = {}) => {
    const response = await apiClient.get('/reports/popular-activities', { params: buildReportParams(filters) });
    return response.data;
  },

  // Get recommendation acceptance rate
  getRecommendationAcceptance: async () => {
    const response = await apiClient.get('/reports/recommendation-acceptance');
    return response.data;
  },

  // Export the full report (charts + raw data) as PDF
  exportPDF: async (filters = {}) => {
    const response = await apiClient.get('/reports/export/pdf', {
      params: buildReportParams(filters),
      responseType: 'blob'
    });
    return response;
  }
};

const instituteAPI = {
  // Get all institutes
  getAll: async () => {
    const response = await apiClient.get('/institutes');
    return response.data;
  },

  // Get single institute by UUID
  getByUuid: async (uuid) => {
    const response = await apiClient.get(`/institutes/${uuid}`);
    return response.data;
  },

  // Create new institute
  create: async (instituteData) => {
    const response = await apiClient.post('/institutes', instituteData);
    return response.data;
  },

  // Update institute
  update: async (uuid, instituteData) => {
    const response = await apiClient.put(`/institutes/${uuid}`, instituteData);
    return response.data;
  },

  // Delete institute
  delete: async (uuid) => {
    const response = await apiClient.delete(`/institutes/${uuid}`);
    return response.data;
  }
};

const authAPI = {
  // What can the current user do: isSuperAdmin, and/or a districtAdmin/counselor
  // access assignment (role, institute, campuses) resolved from the local
  // AccessAssignment table.
  getMyAccess: async () => {
    const response = await apiClient.get('/auth/me/access');
    return response.data;
  }
};

const rosterAPI = {
  // Get roster (district admin's own district, or ?instituteUuid= for superAdmin)
  getAll: async (params = {}) => {
    const response = await apiClient.get('/roster', { params });
    return response.data;
  },

  // Add or upsert a single student
  add: async (studentData, params = {}) => {
    const response = await apiClient.post('/roster', studentData, { params });
    return response.data;
  },

  // Update a student by Student ID
  update: async (studentId, studentData, params = {}) => {
    const response = await apiClient.put(`/roster/${studentId}`, studentData, { params });
    return response.data;
  },

  // Remove a student from the roster
  delete: async (studentId, params = {}) => {
    const response = await apiClient.delete(`/roster/${studentId}`, { params });
    return response.data;
  },

  // Bulk-import students from a CSV file
  bulkImport: async (file, params = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/roster/bulk', formData, {
      params,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

const accessAssignmentAPI = {
  // List admin/counselor assignments for a district
  getAll: async (params = {}) => {
    const response = await apiClient.get('/access-assignments', { params });
    return response.data;
  },

  // Create a single assignment. role defaults to 'counselor' server-side
  // unless the caller is superAdmin.
  create: async (assignmentData, params = {}) => {
    const response = await apiClient.post('/access-assignments', assignmentData, { params });
    return response.data;
  },

  // Update campuses/status on an existing assignment
  update: async (id, assignmentData, params = {}) => {
    const response = await apiClient.put(`/access-assignments/${id}`, assignmentData, { params });
    return response.data;
  },

  // Revoke an assignment
  delete: async (id, params = {}) => {
    const response = await apiClient.delete(`/access-assignments/${id}`, { params });
    return response.data;
  },

  // Bulk-import counselor assignments from a CSV file
  bulkImport: async (file, params = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/access-assignments/bulk', formData, {
      params,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

const chatAPI = {
  // Initialize chat with student context
  initChat: async (studentID, selectedClusters, district) => {
    const response = await apiClient.post('/chat/init', {
      studentID,
      selectedClusters,
      district
    }, {
      timeout: 60000 // 60 second timeout for chat (external API can be slow)
    });
    return response.data;
  },

  // Send message to chat
  sendMessage: async (question, chatId, sessionId = null) => {
    const response = await apiClient.post('/chat/message', {
      question,
      chatId,
      sessionId
    }, {
      timeout: 60000 // 60 second timeout for chat (external API can be slow)
    });
    return response.data;
  }
};

export { studentAPI, surveyAPI, resultsAPI, reportsAPI, instituteAPI, rosterAPI, accessAssignmentAPI, authAPI, chatAPI };
export default apiClient;
